/**
 * Bloque 3 — Fuentes de datos.
 *
 * Es la hoja de ruta de las integraciones y la explicación del banner de datos de
 * demostración: mientras una fuente siga en carga manual, las cifras del panel
 * son inventadas. Cada fuente linkea a **su** panel de administración en el
 * sistema de origen, que es donde se saca la credencial que falta.
 */
import {
  DATA_SOURCE_STATUS_LABELS,
  evaluateFreshness,
  listaEs,
  type DataSource,
  type DeepLinkConfig,
  type Health,
  type MetricDefinition,
  type Provenance,
} from '@ops/contracts'

import {
  Card,
  ExternalLink,
  Icon,
  Panel,
  ProvenanceBadge,
  Section,
  Stack,
  StatusPill,
} from '../../ui'
import { consolaDeFuente } from './consolas'

const MAX_METRICAS_LISTADAS = 6

export interface FuentesPanelProps {
  fuentes: readonly DataSource[]
  /** El catálogo, para nombrar las métricas que alimenta cada fuente. */
  metricas: readonly MetricDefinition[]
  cfg: DeepLinkConfig
  /** Reloj compartido de la pantalla, para que la frescura sea consistente. */
  now: Date
  /**
   * Acción del estado "sin vincular" de una fuente. La consola de una fuente no
   * falta por un id sin cargar sino por una variable de entorno, así que la
   * acción baja al bloque de config de deep links de esta misma pantalla.
   */
  onIrAConfig?: () => void
  id?: string
}

/**
 * Severidad de una fuente. Un error es crítico; "manual" es atención y no
 * crítico: no está roto, está sin hacer.
 */
export function severidadDeFuente(fuente: DataSource): Health {
  if (fuente.status === 'error') return 'critical'
  if (fuente.status === 'manual') return 'warning'
  return 'ok'
}

export function provenanceDeFuente(fuente: DataSource, now: Date): Provenance {
  return {
    source: fuente.id,
    updatedAt: fuente.ultimoSync,
    updatedBy: null,
    freshness: evaluateFreshness({
      source: fuente.id,
      updatedAt: fuente.ultimoSync,
      now,
    }),
  }
}

export function FuentesPanel({
  fuentes,
  metricas,
  cfg,
  now,
  onIrAConfig,
  id,
}: FuentesPanelProps) {
  const nombrePorSlug = new Map(metricas.map((m) => [m.slug, m.nombre]))
  const conectadas = fuentes.filter((f) => f.status === 'conectada').length
  const conError = fuentes.filter((f) => f.status === 'error').length

  if (fuentes.length === 0) {
    return (
      <Section
        id={id}
        kicker="BLOQUE 3"
        titulo="De dónde van a salir los números"
      >
        <Card padding="lg" hundida punteada>
          <p className="text-meta text-muted-strong">
            No hay ninguna fuente declarada. Sin fuentes, todo dato del panel
            entró a mano y no hay forma de saber de dónde salió.
          </p>
        </Card>
      </Section>
    )
  }

  return (
    <Section
      id={id}
      kicker="BLOQUE 3"
      titulo="De dónde van a salir los números"
      bajada={
        <>
          <b>
            {conectadas} de {fuentes.length}
          </b>{' '}
          fuentes conectadas
          {conError > 0 ? (
            <>
              {' '}
              y <b>{conError}</b> con error
            </>
          ) : null}
          . Mientras quede una en carga manual, el banner de datos de
          demostración sigue prendido: lo prende el servidor contando estas
          fuentes, no una constante del front.
        </>
      }
    >
      <Stack as="ul" gap="md">
        {fuentes.map((fuente) => {
          const severidad = severidadDeFuente(fuente)
          const consola = consolaDeFuente(cfg, fuente.id)
          const nombres = fuente.alimenta.map(
            (slug) => nombrePorSlug.get(slug) ?? slug,
          )
          const listadas = nombres.slice(0, MAX_METRICAS_LISTADAS)
          const resto = nombres.length - listadas.length

          return (
            <li key={fuente.id}>
              <Panel
                titulo={fuente.nombre}
                severidad={severidad}
                acciones={
                  <StatusPill severidad={severidad} size="sm">
                    {DATA_SOURCE_STATUS_LABELS[fuente.status]}
                  </StatusPill>
                }
                footer={
                  consola.sistema === null ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Icon name="lapiz" size={11} />
                      La consola de esta fuente es este panel: se carga en el
                      catálogo de métricas.
                    </span>
                  ) : (
                    <span className="inline-flex flex-wrap items-center gap-2">
                      <ExternalLink
                        link={consola.link}
                        variante="chip"
                        sistemaFaltante={consola.sistemaLabel ?? undefined}
                        onVincular={
                          consola.faltante === null || onIrAConfig === undefined
                            ? undefined
                            : onIrAConfig
                        }
                        // El nombre no repite la variable: el aviso de al lado ya
                        // dice cuál falta, y el bloque de config es uno solo.
                        textoVincular="Completar la config"
                      />
                      {consola.link === null && consola.faltante !== null ? (
                        <span className="text-micro text-warning-text">
                          Falta configurar <code>{consola.faltante}</code> más
                          abajo.
                        </span>
                      ) : null}
                    </span>
                  )
                }
              >
                <Stack gap="sm">
                  <ProvenanceBadge
                    provenance={provenanceDeFuente(fuente, now)}
                    now={now}
                  />

                  <p className="text-meta text-muted-strong">
                    {nombres.length === 0 ? (
                      'Todavía no alimenta ninguna métrica del catálogo.'
                    ) : (
                      <>
                        Alimenta{' '}
                        <b className="font-semibold text-text">
                          {nombres.length}
                        </b>{' '}
                        {nombres.length === 1 ? 'métrica' : 'métricas'}:{' '}
                        {listaEs(listadas)}
                        {resto > 0 ? ` y ${resto} más` : ''}.
                      </>
                    )}
                  </p>

                  {fuente.ultimoError !== null ? (
                    <p className="inline-flex items-start gap-1.5 rounded border border-danger bg-danger-soft px-2 py-1 text-meta text-danger-text">
                      <Icon name="critico" size={12} className="mt-px" />
                      <span>
                        <b>Último error:</b> {fuente.ultimoError}
                      </span>
                    </p>
                  ) : null}

                  {fuente.pendiente !== null ? (
                    <p className="max-w-prosa text-meta text-muted-strong">
                      <b className="font-semibold text-text">Qué falta:</b>{' '}
                      {fuente.pendiente}
                    </p>
                  ) : null}
                </Stack>
              </Panel>
            </li>
          )
        })}
      </Stack>
    </Section>
  )
}
